import type { TFunction } from "i18next";

import type { IsoDate } from "@thalamos/common";
import {
  presentationOf,
  SubmitFormDraftRequestSchema,
  type Assessment,
  type SubmitFormDraftRequest,
  type UserId,
} from "@thalamos/haven-common";

import { itemCommentLabel } from "../../components/ItemComment/ItemComment.js";
import { FORM_LANGUAGE, type OpenedForm } from "./AssessmentContext.js";

/**
 * The request sending a form's draft as finally answered, with the words its
 * items were shown in. Undefined where it cannot be built, which nothing
 * underneath has changed to explain, so the send fails rather than being
 * refused.
 */
export const submissionRequestOf = ({
  opened: { schema, form, draft },
  assessment,
  assessedOn,
  approverUserId,
  t,
}: {
  opened: OpenedForm;
  assessment: Pick<Assessment, "instrument">;
  assessedOn: IsoDate;
  approverUserId?: UserId | undefined;
  t: TFunction;
}): SubmitFormDraftRequest | undefined => {
  const presentation = presentationOf({
    schema,
    form,
    document: draft.document,
    context: draft.context,
    source: assessment.instrument,
    requestedLanguage: FORM_LANGUAGE,
    commentLabelOf: (_element, answered) =>
      itemCommentLabel(t, FORM_LANGUAGE, answered),
  });

  if (presentation.isErr()) {
    return undefined;
  }

  const request = SubmitFormDraftRequestSchema.safeParse({
    document: draft.document,
    presentation: presentation.value,
    assessedOn,
    approverUserId,
  });

  // Unreachable while the document comes from the parsed draft, the date and
  // approver arrive branded from the dialog, and the presentation is built
  // from a parsed instrument.
  /* v8 ignore next */
  return request.success ? request.data : undefined;
};
